import React, { useState, useRef, useEffect, useCallback } from 'react'
import { api } from '../../apiUtils'
import styles from './styles.module.scss'

interface Message {
  id: number
  text: string
  sender: 'user' | 'bot'
}

interface TopicCategory {
  id: string
  label: string
}

const quickCategories: TopicCategory[] = [
  { id: 'ugc', label: '🔎 UGC & Fonctionnement' },
  { id: 'campagne', label: '🎬 Campagnes' },
  { id: 'deal', label: '🤝 Deals & Offres' },
  { id: 'droits', label: '📄 Droits & contenus' },
  { id: 'paiement', label: '💲 Prix & paiement' },
  { id: 'contact', label: '📩 Parler à quelqu\'un' },
]

const quickQuestions: Record<string, string[]> = {
  ugc: ["Qu'est-ce que l'UGC ?", "Qui sont les créateurs ?", "Comment contacter un créateur ?", "Comment fonctionne la modération ?"],
  campagne: ["Qu'est-ce qu'une campagne ?", "Comment fonctionne une campagne ?", "Combien de temps dure une campagne ?", "Comment fixer le prix ?"],
  deal: ["Comment fonctionnent les deals ?", "Comment annuler un deal ?", "Le créateur ne répond plus"],
  droits: ["Les contenus sont-ils libres de droit ?", "Combien de temps suis-je propriétaire ?", "Puis-je faire de la pub ?"],
  paiement: ["Comment fonctionne le paiement ?", "Puis-je annuler mon abonnement ?", "Comment me faire rembourser ?"],
  contact: [],
}

const HIDDEN_KEY = 'faqChatbotHidden'

const FaqChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [hidden, setHidden] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HIDDEN_KEY) === '1'
    } catch {
      return false
    }
  })
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showQuickTopics, setShowQuickTopics] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const msgIdRef = useRef(0)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const addBotMessage = useCallback((text: string) => {
    msgIdRef.current += 1
    setMessages(prev => [...prev, { id: msgIdRef.current, text, sender: 'bot' }])
  }, [])

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim()) return

    msgIdRef.current += 1
    setMessages(prev => [...prev, { id: msgIdRef.current, text: text.trim(), sender: 'user' }])
    setInput('')
    setShowQuickTopics(false)
    setSelectedCategory(null)
    setIsLoading(true)

    try {
      const res = await api.post('/faq/chat', { message: text.trim() })
      addBotMessage(res.data.answer)
    } catch {
      addBotMessage("Désolé, une erreur est survenue. Contactez-nous à **contact@vogz.io** 📩")
    } finally {
      setIsLoading(false)
    }
  }, [addBotMessage])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendMessage(input)
  }

  const handleCategoryClick = (cat: TopicCategory) => {
    if (cat.id === 'contact') {
      sendMessage("Je souhaite parler à quelqu'un")
      return
    }
    setSelectedCategory(cat.id)
    setShowQuickTopics(false)
  }

  const handleQuestionClick = (question: string) => {
    sendMessage(question)
  }

  const handleBack = () => {
    setSelectedCategory(null)
    setShowQuickTopics(true)
  }

  const handleToggle = () => {
    setIsOpen(prev => {
      if (!prev && messages.length === 0) {
        setTimeout(() => {
          msgIdRef.current += 1
          setMessages([{
            id: msgIdRef.current,
            text: "Bonjour ! 👋 Je suis l'assistant Vogz. Sélectionnez un sujet ou posez-moi directement votre question.",
            sender: 'bot'
          }])
        }, 300)
      }
      return !prev
    })
  }

  const formatMessage = (text: string) => {
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  }

  const handleHide = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(false)
    setHidden(true)
    try {
      localStorage.setItem(HIDDEN_KEY, '1')
    } catch {
      /* noop */
    }
  }

  const handleRestore = () => {
    setHidden(false)
    try {
      localStorage.removeItem(HIDDEN_KEY)
    } catch {
      /* noop */
    }
  }

  if (hidden) {
    return (
      <button
        type="button"
        className={styles.restoreHandle}
        onClick={handleRestore}
        title="Afficher l'assistant Vogz"
      >
        <i className="fa-solid fa-headset" />
      </button>
    )
  }

  return (
    <div className={styles.chatbotWrapper}>
      {isOpen && (
        <div className={styles.chatPanel}>
          <div className={styles.chatHeader}>
            <div className={styles.headerInfo}>
              <div className={styles.headerDot} />
              <span>Assistant Vogz</span>
            </div>
            <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          <div className={styles.chatMessages}>
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`${styles.message} ${msg.sender === 'user' ? styles.userMessage : styles.botMessage}`}
              >
                <div
                  className={styles.messageBubble}
                  dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }}
                />
              </div>
            ))}

            {isLoading && (
              <div className={`${styles.message} ${styles.botMessage}`}>
                <div className={styles.messageBubble}>
                  <div className={styles.typingIndicator}>
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}

            {showQuickTopics && messages.length > 0 && (
              <div className={styles.quickTopics}>
                {quickCategories.map(cat => (
                  <button
                    key={cat.id}
                    className={styles.topicBtn}
                    onClick={() => handleCategoryClick(cat)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            )}

            {selectedCategory && quickQuestions[selectedCategory] && (
              <div className={styles.quickTopics}>
                <button className={styles.backBtn} onClick={handleBack}>
                  ← Retour
                </button>
                {quickQuestions[selectedCategory].map(q => (
                  <button
                    key={q}
                    className={styles.topicBtn}
                    onClick={() => handleQuestionClick(q)}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <form className={styles.chatInput} onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Posez votre question..."
              disabled={isLoading}
            />
            <button type="submit" disabled={isLoading || !input.trim()}>
              <i className="fa-solid fa-paper-plane" />
            </button>
          </form>
        </div>
      )}

      <div className={styles.fabContainer}>
        {!isOpen && (
          <button
            type="button"
            className={styles.dismissBadge}
            onClick={handleHide}
            title="Masquer l'assistant"
            aria-label="Masquer l'assistant"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        )}
        <button
          className={`${styles.fabButton} ${isOpen ? styles.fabOpen : ''}`}
          onClick={handleToggle}
        >
          {isOpen
            ? <i className="fa-solid fa-xmark" />
            : <i className="fa-solid fa-comment-dots" />
          }
        </button>
      </div>
    </div>
  )
}

export default FaqChatbot
